import type { useForm } from "laravel-precognition-vue";
import tippy from "tippy.js";
const defaultErrorType = "div";
export default defineNuxtPlugin((nuxtApp) => {
  const addError = (el: HTMLInputElement, error: string) => {
    el.classList.add("is-invalid");
    const errorType = el.getAttribute("error-type") || defaultErrorType;
    if (errorType === "tippy") {
      addTippy(el, error);
    } else {
      addErrorDiv(el, error);
    }
  };
  const addErrorDiv = (el: HTMLElement, error: string) => {
    const id = `precog-${el.getAttribute("id")}-error`;
    let div = document.getElementById(id);
    if (!div) {
      div = document.createElement("div");
      div.classList.add("error");
      div.id = `precog-${el.getAttribute("id")}-error`;
      // add div to parent
      el.parentElement?.appendChild(div);
    }
    div.innerText = error;
  };
  const removeErrorDiv = (el: HTMLElement) => {
    const div = document.getElementById(`precog-${el.getAttribute("id")}-error`);
    if (div) {
      div.remove();
    }
  };
  const removeError = (el: HTMLInputElement) => {
    el.classList.remove("is-invalid");
    const errorType = el.getAttribute("error-type") || defaultErrorType;
    if (errorType === "tippy") {
      removeTippy(el);
    } else {
      removeErrorDiv(el);
    }
  };
  const addTippy = (el: HTMLElement, error: string) => {
    const instance = (el as any)._tippy;
    if (!instance) {
      tippy(el, {
        content: error,
        trigger: "focus",
        placement: "bottom",
        sticky: true,
      }).show();
    } else {
      instance.setContent(error);
      instance.show();
    }
  };
  const removeTippy = (el: HTMLElement) => {
    const instance = (el as any)._tippy;
    if (instance) {
      instance.destroy();
    }
  };

  const updated = (el: HTMLInputElement, binding: any) => {
    const form: ReturnType<typeof useForm> = binding.value as any;
    const attr: string = el.getAttribute("id") || "";

    if (form.errors[attr]) {
      addError(el, form.errors[attr]);
    } else {
      removeError(el);
    }
  };

  nuxtApp.vueApp.directive("precog-error", {
    mounted(el: HTMLInputElement, binding) {
      const form: ReturnType<typeof useForm> = binding.value as any;
      const attr: string = el.getAttribute("id") || "";
      if (!attr) {
        throw new Error("precog-error directive must be used on an input element with a name attribute");
      }

      if (form[attr] === undefined) {
        throw new Error(
          `precog-error ${attr} is not a valid attribute of the form. Make sure you have a field with the name ${attr}`,
        );
      }
      let validateOn = el.getAttribute("validate-on");
      if (!validateOn) {
        validateOn = "blur";
      }
      // validateOn must be one of "blur", "change", or "input"
      if (!["blur", "change", "input"].includes(validateOn)) {
        throw new Error(`precog-error for ${attr} validate-on attribute must be one of 'blur', 'change', or 'input'`);
      }
      if (validateOn === "input") {
        let debounceTimeout: NodeJS.Timeout | null = null;
        el.addEventListener("input", () => {
          if (debounceTimeout) {
            clearTimeout(debounceTimeout);
          }
          debounceTimeout = setTimeout(() => {
            form.validate(attr, {
              onFinish: () => {
              },
              onSuccess: () => {
                form.forgetError(attr);
              },
            });
          }, 300); // Adjust debounce delay as needed
        });
      } else {
        el.addEventListener(validateOn, () => {
          form.validate(attr, {
            onFinish: () => {
            },
            onSuccess: () => {
              form.forgetError(attr);
            },
          });
        });
      }

      form.validator().on("errorsChanged", () => {
        updated(el, binding);
      });
    },
  });

  // Register more directives as needed
});
